import { Injectable, OnDestroy } from '@angular/core';

import { Subject } from 'rxjs';

import { Project, ProjectType } from '@mirage-projects/cw-models';

import { MessageDialogOptions, MessageDialogOptionsButton, MessageDialogService } from '../message-dialog/message-dialog.service';

/** This service communicates the component ShowRelatedProjects with the calling component */
@Injectable()
export class ShowRelatedProjectsService implements OnDestroy {
    private readonly openProjectViewerUrlSubject$ = new Subject<{ id: number; projectType: ProjectType }>();
    readonly openProjectViewerUrl$ = this.openProjectViewerUrlSubject$.asObservable();

    private readonly dettachObjectFromProjectSubject$ = new Subject<number>();
    readonly dettachObjectFromProject$ = this.dettachObjectFromProjectSubject$.asObservable();

    private readonly projectListSubject$ = new Subject<Project[]>();
    readonly projectList$ = this.projectListSubject$.asObservable();

    private readonly errorMessageSubject$ = new Subject<string>();
    readonly errorMessage$ = this.errorMessageSubject$.asObservable();

    private readonly isLoadingSubject$ = new Subject<boolean>();
    readonly isLoading$ = this.isLoadingSubject$.asObservable();

    constructor(private messageDialogService: MessageDialogService) {}

    ngOnDestroy(): void {
        this.openProjectViewerUrlSubject$.complete();
        this.dettachObjectFromProjectSubject$.complete();
    }

    updateProjectList(projects: Project[]): void {
        this.projectListSubject$.next(projects);
    }

    updateErrorMessage(error: string): void {
        this.errorMessageSubject$.next(error);
    }

    updateLoadingStatus(isLoading: boolean): void {
        this.isLoadingSubject$.next(isLoading);
    }

    openProjectViewer({ id, projectType }: Project): void {
        this.openProjectViewerUrlSubject$.next({ id, projectType });
    }

    async dettachObjectFromProject(id: number): Promise<void> {
        this.updateLoadingStatus(true);

        const isConfirmed = await this.showDettachObjectWarning();

        if (!isConfirmed) {
            this.updateLoadingStatus(false);
            return;
        }

        this.dettachObjectFromProjectSubject$.next(id);
    }

    private async showDettachObjectWarning(): Promise<boolean> {
        const acceptButton = this.acceptButton;
        const cancelButton = this.messageDialogService.getCancelButtonConfig();
        const options: Partial<MessageDialogOptions> = {
            style: 'primary',
            headerText: 'RELATED_PROJECTS.REMOVE_FROM_PROJECT',
            contentText: 'RELATED_PROJECTS.REMOVE_OBJECT_FROM_PROJECT.DESCRIPTION',
            buttonsList: [cancelButton, acceptButton],
        };

        const resultBtnCfg = await this.messageDialogService.showInformation(options);

        return resultBtnCfg.id === acceptButton.id;
    }

    private get acceptButton(): MessageDialogOptionsButton {
        return {
            id: 'delete',
            text: 'GENERAL.DELETE',
            primary: true,
        };
    }
}
